import {
  type AiSuggestionView,
  extractTaskCriteria,
  type GitChangeRequestView,
  GitTaskCoverageOutput,
  REVIEW_TASK_COVERAGE_SKILL,
} from '@apm/contracts';
import { ListChecks } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { displayDateTime } from '../../../lib/date';
import { AiSkillPanel } from '../../ai/components/ai-skill-panel';
import { type GitDevelopmentSlotProps } from '../types';
import { GitChangeRequestBadge } from './git-marks';

/**
 * Đối chiếu tiêu chí của việc với MR đã gắn — APM-GIT-n, docs/plan-git-integration.md
 * §8.5. Cắm vào `GIT_DEVELOPMENT_SLOTS` (khối "Phát triển" của task).
 *
 * - **Đường làm tay** (luôn có, không cần key AI): bảng tiêu chí đọc từ mô tả
 *   (`extractTaskCriteria`) đặt cạnh danh sách MR đã gắn.
 * - **AI** (`review_task_coverage`): gợi ý ở `ai_suggestion` (`pending`) — nhãn
 *   "Gợi ý của AI — chưa ai duyệt", kèm "Chỉ kiểm lời mô tả của MR" khi lượt chạy
 *   không đọc mã (kho chưa bật đọc diff). Không sửa việc, không đổi trạng thái,
 *   không chặn merge: khung chỉ có "Bỏ qua".
 * - Chỉ việc (`task`), không issue: skill đối chiếu tiêu chí nghiệm thu của task.
 *
 * Nhận `asOf` (luật #7) và **không hiện gì** khi đang xem quá khứ: gợi ý đối chiếu
 * nói về MR và mô tả **hiện tại** (`GET /ai/suggestions` không có mốc), còn mô tả
 * mà khối nhận cũng là bản hôm nay — đặt chúng cạnh danh sách MR tại một mốc cũ là
 * hai trục thời gian trên một khối mà không ai giải thích được cái nào thắng.
 */
export function GitTaskCoverageSlot({
  projectId,
  target,
  links,
  asOf,
  permissions,
  description,
}: GitDevelopmentSlotProps): ReactNode {
  if (target.kind !== 'task' || asOf !== undefined || !permissions.read) return null;
  return (
    <TaskCoverage
      projectId={projectId}
      taskId={target.id}
      changeRequests={links.changeRequests}
      description={description}
    />
  );
}

function TaskCoverage({
  projectId,
  taskId,
  changeRequests,
  description,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly changeRequests: readonly GitChangeRequestView[];
  readonly description: string | null | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const criteria = description === undefined ? null : extractTaskCriteria(description);

  return (
    <AiSkillPanel
      skill={REVIEW_TASK_COVERAGE_SKILL}
      projectId={projectId}
      icon={ListChecks}
      title={t('gitTab.coverage.title')}
      help={t('gitTab.coverage.help')}
      label={t('gitTab.coverage.run')}
      prompt={t('gitTab.coverage.prompt')}
      params={{ taskId }}
      schema={GitTaskCoverageOutput}
      routeParams={{ projectId }}
      select={(suggestion) => coverageTaskId(suggestion) === taskId}
      controls={<ManualTable criteria={criteria} changeRequests={changeRequests} />}
      render={(content, suggestion) => (
        <CoverageResult content={content} suggestion={suggestion} changeRequests={changeRequests} />
      )}
    />
  );
}

/** `taskId` mà `finalize()` của skill đặt vào nội dung gợi ý. */
export function coverageTaskId(suggestion: AiSuggestionView): string | null {
  const content = suggestion.content;
  if (content === null || typeof content !== 'object') return null;
  const taskId = (content as { taskId?: unknown }).taskId;
  return typeof taskId === 'string' ? taskId : null;
}

/** Đường làm tay: tiêu chí của việc cạnh MR đã gắn. Thuần trình bày. */
function ManualTable({
  criteria,
  changeRequests,
}: {
  readonly criteria: readonly string[] | null;
  readonly changeRequests: readonly GitChangeRequestView[];
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="git-coverage-manual">
      {criteria !== null ? (
        <div className="min-w-0">
          <p className="mb-1 text-xs font-medium">{t('gitTab.coverage.criteriaHeading')}</p>
          {criteria.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('gitTab.coverage.noCriteriaManual')}</p>
          ) : (
            <ol className="flex list-decimal flex-col gap-0.5 pl-5 text-sm">
              {criteria.map((item, index) => (
                <li key={index} className="break-words">
                  {item}
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : null}
      <div className="min-w-0">
        <p className="mb-1 text-xs font-medium">{t('gitTab.coverage.changeRequestsHeading')}</p>
        {changeRequests.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('gitTab.coverage.noChangeRequests')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {changeRequests.map((cr) => (
              <li key={cr.id} className="flex min-w-0 items-center gap-1.5">
                <GitChangeRequestBadge changeRequest={cr} />
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  !{cr.number}
                </span>
                <span className="truncate">{cr.title}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Kết quả AI: tiêu chí chưa thấy trước, rồi tiêu chí đã thấy kèm câu trích. Thuần trình bày. */
function CoverageResult({
  content,
  suggestion,
  changeRequests,
}: {
  readonly content: GitTaskCoverageOutput;
  readonly suggestion: AiSuggestionView;
  readonly changeRequests: readonly GitChangeRequestView[];
}): ReactNode {
  const { t } = useTranslation();
  const missing = content.criteria.filter((item) => !item.seen);
  const seen = content.criteria.filter((item) => item.seen);
  const checkedKeys = new Set(content.checked.map((cr) => `${cr.repositoryId}#${cr.number}`));
  const newer = changeRequests.filter((cr) => !checkedKeys.has(`${cr.repositoryId}#${cr.number}`));

  return (
    <div className="flex flex-col gap-2" data-testid="git-coverage-result">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="rounded-full border border-rag-amber/60 bg-rag-amber/10 px-2 py-0.5 text-foreground">
          {t('gitTab.coverage.status')}
        </span>
        <span
          className="rounded-full border border-border px-2 py-0.5 text-muted-foreground"
          data-testid="git-coverage-basis"
        >
          {content.basedOn === 'diff'
            ? t('gitTab.coverage.basis.diff')
            : t('gitTab.coverage.basis.description')}
        </span>
        <span className="text-muted-foreground">
          {t('gitTab.coverage.at', { at: displayDateTime(suggestion.createdAt) })}
        </span>
      </div>

      <p className="text-sm">{content.summary}</p>

      {content.criteria.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('gitTab.coverage.noCriteria')}</p>
      ) : null}

      {missing.length > 0 ? (
        <div className="rounded-lg border-l-4 border-rag-amber bg-background px-3 py-2">
          <p className="text-xs font-medium">{t('gitTab.coverage.missingHeading')}</p>
          <ul className="mt-0.5 flex list-disc flex-col gap-0.5 pl-5 text-sm">
            {missing.map((item, index) => (
              <li key={index} className="break-words">
                {item.criterion}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {seen.length > 0 ? (
        <div className="rounded-lg border-l-4 border-rag-green bg-background px-3 py-2">
          <p className="text-xs font-medium">{t('gitTab.coverage.seenHeading')}</p>
          <ul className="mt-0.5 flex flex-col gap-1 text-sm">
            {seen.map((item, index) => (
              <li key={index} className="break-words">
                {item.criterion}
                {item.evidence ? (
                  <span className="block text-xs text-muted-foreground">
                    {t('gitTab.coverage.evidence', {
                      number: item.evidence.number,
                      quote: item.evidence.quote,
                    })}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground">
        {content.checked.length > 0
          ? t('gitTab.coverage.checked', {
              list: content.checked.map((cr) => `!${cr.number}`).join(', '),
            })
          : t('gitTab.coverage.checkedNone')}
      </p>
      {newer.length > 0 ? (
        <p className="text-xs text-rag-amber" data-testid="git-coverage-newer">
          {t('gitTab.coverage.newSince', { list: newer.map((cr) => `!${cr.number}`).join(', ') })}
        </p>
      ) : null}
    </div>
  );
}
